// Imports for external libraries go here.
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { EditableComponent } from '@adobe/aem-react-editable-components';
// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { Button } from '@marriott/mi-ui-library';
import { CardComparisonProps, CardComparisonCategory, CardComparisonData } from './CardComparison.types';
import { StyledCardComparison } from './CardComparison.styles';
import { TableRow } from '../../molecules/DataCellComponent';
import { usePageContext } from '../../context/PageContext';
import { setCardArray } from '../../modules/utils';
import { CardComparisonAccordion } from '../../molecules/CardComparisonAccordion';
import { SESSION_CONSTANTS } from '../../modules/constants';
export const CardComparisonConfig = {
  emptyLabel: 'CardComparison',
  isEmpty: () => true,
  resourceType: `/components/content/CardComparison`,
};

const sortByCardOrder = (data: CardComparisonData[], order: string[]) => {
  return [...data].sort((a, b) => order.indexOf(a.cardId ?? '') - order.indexOf(b.cardId ?? ''));
};

const sortCategories = (categories: CardComparisonCategory[], filteredCategoryIds: string[]) =>
  categories
    .filter(category => filteredCategoryIds.includes(category.categoryId ?? ''))
    .sort((a, b) => (a.categoryId ?? '').localeCompare(b.categoryId ?? ''));

const groupCategories = (categories: CardComparisonCategory[]) =>
  categories
    .reduce<{ id: string; value: string; count: number }[]>((acc, obj, index) => {
      const exists = acc.some(item => item.id === obj.groupId && item.value === obj.groupValue);
      if (!exists) {
        acc.push({ id: obj.groupId ?? 'unknown', value: obj.groupValue ?? '', count: index });
      }
      return acc;
    }, [])
    .sort((a, b) => a.id.localeCompare(b.id))
    .filter(group => group.id !== 'Header');

// Use named rather than default exports.
export const CardComparisonComp: FC<CardComparisonProps> = memo((props: CardComparisonProps) => {
  const { linkText, linkUrl, openInNewTab, trackingProperties } = props;
  const [prevEndPoint, setPrevEndPoint] = useState(linkUrl);
  const linkType = openInNewTab ? 'external' : 'internal';
  const clickTrackValue = `${trackingProperties?.trackingContentPosition}|${trackingProperties?.trackingDescription}|${linkType}|${trackingProperties?.additionalTrackingVariables}`;

  useEffect(() => {
    const tableElement = document.querySelector('.table-container');
    if (!tableElement) {
      return undefined;
    }

    const handleScroll = () => {
      const tableHeaderThreshold = tableElement.getBoundingClientRect()?.top ?? 0;
      const shouldHide = window.scrollY > tableHeaderThreshold;
      const elementsToHide = document.querySelectorAll(
        '.header-category, .platinum-status, .gold-status, .silver-status, .card-image, .vertical-card-image'
      );
      elementsToHide.forEach(el => {
        el.classList.toggle('d-none', shouldHide);
        el.classList.toggle('d-md-block', shouldHide);
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const creditCardEndPointFromSessionStorage = sessionStorage?.getItem(SESSION_CONSTANTS.CREDIT_CARD_ENDPOINT);
      if (creditCardEndPointFromSessionStorage) {
        setPrevEndPoint(JSON.parse(creditCardEndPointFromSessionStorage));
      }
    }
  }, []);

  const { cardtype1, cardtype2, cardtype3 } = usePageContext();

  const cardIdFromURL = useMemo(
    () => [cardtype1, cardtype2, cardtype3].filter(Boolean),
    [cardtype1, cardtype2, cardtype3]
  );

  const selectedCategories = useMemo(
    () => (props.idValues.includes(',') ? props.idValues.split(',') : []),
    [props.idValues]
  );

  const categoryData = useMemo<CardComparisonCategory[]>(() => {
    const cats = props?.pageItems?.categories ? Object.values(props.pageItems.categories) : [];
    return cats as CardComparisonCategory[];
  }, [props?.pageItems?.categories]);

  const cardData = useMemo(
    () =>
      setCardArray(props?.pageItems).map(data => ({
        ...data,
        cardType: data.cardType ?? 'none',
      })),
    [props?.pageItems]
  );

  const filteredCategoryIds = useMemo(
    () => (selectedCategories.length ? selectedCategories : categoryData.map(category => category.categoryId ?? '')),
    [selectedCategories, categoryData]
  );

  const selectedCardsData = useMemo(
    () => cardData.filter(data => cardIdFromURL.includes(data.cardId ?? '')),
    [cardData, cardIdFromURL]
  );

  const sortedCategory = useMemo(
    () =>
      sortCategories(categoryData, filteredCategoryIds).filter(category =>
        selectedCardsData.some(data => data.categoryId === category.categoryId && data.dataType !== 'emptyField')
      ),
    [categoryData, filteredCategoryIds, selectedCardsData]
  );

  const sortedCategoryGroups = useMemo(() => groupCategories(sortedCategory), [sortedCategory]);

  const dataJson = useMemo(() => sortByCardOrder(selectedCardsData, cardIdFromURL), [selectedCardsData, cardIdFromURL]);

  const getDataList = useCallback(
    (category: CardComparisonCategory) => {
      const data = dataJson.filter(item => item.categoryId === category.categoryId);
      return data;
    },
    [dataJson]
  );

  const groupedCategories = useCallback(
    (categoryList: CardComparisonCategory[], groupId: string) =>
      categoryList.filter(category => category.groupId === groupId && category.groupId !== 'Header'),
    []
  );

  const headerRow = useMemo(() => sortedCategory.filter(category => category.groupId === 'Header'), [sortedCategory]);

  return (
    <StyledCardComparison data-component-name="o-cobrand-CardComparison" data-testid="cobrand-CardComparison">
      <div className={'col-12 p-0'}>
        <div className="d-flex justify-content-center justify-content-md-start">
          <Button
            isLink={true}
            href={prevEndPoint}
            className={'m-button-secondary-icon m-button-m my-5'}
            testId="backbtn"
            linkType={linkType}
            trackingProperties={trackingProperties}
            custom_click_track_value={clickTrackValue}
          >
            <span className="icon-back-arrow" />
            {linkText}
          </Button>
        </div>
        <div className="table-container col-12 p-0 mt-3 mb-5">
          {dataJson && dataJson.length ? (
            <div className="table-wrapper col-12 p-0">
              {headerRow.map((category: CardComparisonCategory) => {
                const dataList = getDataList(category);
                return (
                  <div
                    key={category.categoryId ?? category.groupId ?? category.categoryHeading}
                    className={`table-header align-items-center sticky-header`}
                  >
                    <TableRow
                      category={category}
                      dataList={dataList}
                      type="header"
                      cardIdFromURL={cardIdFromURL}
                      customClass="header-category"
                    />
                  </div>
                );
              })}
              {sortedCategoryGroups.map((group: { id: string; value: string; count: number }) => {
                const categoriesToBeShown = groupedCategories(sortedCategory, group.id);
                return (
                  <CardComparisonAccordion
                    categoriesToBeShown={categoriesToBeShown}
                    group={group}
                    pageItems={props?.pageItems}
                    styleClass={'accordion-wrapper'}
                  />
                );
              })}
            </div>
          ) : (
            <div className="skeleton-loader ">
              <Skeleton height="2.5rem" width="100%" />
            </div>
          )}
        </div>
        <div className={'d-flex justify-content-center'}>
          <Button isLink={true} href="#" className={'my-5'} testId="buttonToTop">
            Return to Top
            <span className="icon-up-arrow ml-1" />
          </Button>
        </div>
      </div>
    </StyledCardComparison>
  );
});

export const CardComparison = (props: CardComparisonProps) => (
  <EditableComponent config={CardComparisonConfig} {...props}>
    <CardComparisonComp {...props} />
  </EditableComponent>
);
